// ProgrammeStepScreen.tsx — opens a single step in the right viewer and wires
// it to progress (resume position, completion, reflection answers) and to the
// next step in the ordered journey.
import React from 'react';
import { VideoLesson } from './VideoLesson';
import { LessonText } from './LessonText';
import { ReflectionPrompt } from './ReflectionPrompt';
import { ModuleCelebration } from './ModuleCelebration';
import { PauseLanding } from './PauseLanding';
import { useProgress } from '@/hooks/useProgress';
import { ReviewCommentButton } from '@/flows/review/ReviewCommentButton';
import { useGuide } from '@/flows/guide/useGuide';
import { MILESTONE_FACE_EVERY_N } from '@/data/guideMessages';
import type { GuideSelection } from '@/services/guidePresence';
import type { Programme, Step } from '@/types';
import type { Handoff } from '@/data/pacing.generated';
import { chapterLoadFor } from '@/data/pacing.generated';
import { landingFor } from '@/services/pausePacing';
import { moduleOfStep, moduleName, nextStepAfter, chapterLocator } from '@/services/contentProvider';
import { daysRequired } from '@/services/compassionDaily';

export function ProgrammeStepScreen({
  programme,
  step,
  onClose,
  onStartBreathing,
  onSelfCompassion,
  onOpenMeditations,
  onOpenStep,
  onExitToToday,
  onModuleComplete,
  onChapterComplete,
}: {
  programme: Programme;
  step: Step;
  onClose: () => void;
  /** Open the Calm Your Body breathing flow (used by the post-module check-in). */
  onStartBreathing?: (protocolId: string) => void;
  /** Switch to the self-compassion route (offered in the post-module check-in). */
  onSelfCompassion?: () => void;
  /** Open the Meditations list (a pause-point handoff). */
  onOpenMeditations?: () => void;
  /** Open a specific step — used by a pause landing's "Keep going" to advance. */
  onOpenStep?: (step: Step) => void;
  /** Leave the lesson flow and land on Today — a pause landing's "I'm done for now". */
  onExitToToday?: () => void;
  /** Fired when finishing this step completes its chapter — so the home screen
   *  can reload at the top when the user returns. */
  onModuleComplete?: () => void;
  /** Fired when finishing this step completes its CHAPTER — drives the once-per-chapter
   *  feedback card (shown after the chapter's celebration). Carries the chapter/module id. */
  onChapterComplete?: (chapterId: string) => void;
}) {
  const progress = useProgress();
  const guide = useGuide();
  const [celebrate, setCelebrate] = React.useState<{ nextModuleTitle?: string; guide?: GuideSelection | null } | null>(null);
  const [landing, setLanding] = React.useState<{ kind: 'pause' | 'skillExit'; handoff?: Handoff } | null>(null);
  const [showReflection, setShowReflection] = React.useState(false);
  const module = moduleOfStep(programme, step.id);
  if (!module) {
    onClose();
    return null;
  }
  // Human-readable locator that names the act and uses a contiguous per-act chapter
  // number (so "Chapter 1" in Act 3 reads sensibly as "<Act III> · Chapter 1").
  const loc = chapterLocator(programme, module.id);
  const chapterLabel = loc.actName ? `${loc.actName} · Chapter ${loc.chapterNo}` : `Chapter ${loc.chapterNo}`;
  const isComplete = progress.isStepComplete(step.id);
  // Review Mode: a comment affordance on the lesson as presented (real step id).
  const stepLabel = [step.title.pre, step.title.em, step.title.post].filter(Boolean).join('').trim();
  const reviewBtn = <ReviewCommentButton targetId={step.id} kind="step" label={stepLabel} />;

  const handleComplete = (opts?: { toToday?: boolean }) => {
    // Multi-day held step (self-compassion): a step whose day label spans N days
    // must be practised on N distinct calendar days before it completes and the
    // next day-step unlocks. Until then, finishing today records a practice day and
    // rests the user ("that's enough for today") without advancing the course.
    const need = daysRequired(step);
    if (need > 1 && !progress.isStepComplete(step.id) && !progress.state.devMode) {
      const daysDone = progress.recordPracticeDay(step.id);
      if (daysDone < need) {
        if (onExitToToday) onExitToToday(); else onClose();
        return;
      }
      // enough days now — fall through to normal completion below.
    }

    const moduleStepIds = module.steps.map((s) => s.id);
    // Does finishing this step complete the whole chapter for the first time?
    const completesModule =
      !progress.isModuleComplete(module.id) &&
      moduleStepIds.every((id) => id === step.id || progress.isStepComplete(id));

    progress.markStepComplete(step.id, { moduleId: module.id, moduleStepIds });
    // Feedback is asked once per CHAPTER (not per step). It fires when a chapter
    // completes; App shows the card after the chapter's celebration, and yields to
    // an act survey at act boundaries.
    if (completesModule) onChapterComplete?.(module.id);
    // Exercises are auto-saved to Favourites on completion, so they're easy to
    // return to and re-use.
    const titleStr = `${step.title.pre}${step.title.em || ''}`;
    if (/exercise/i.test(titleStr)) progress.addFavourite(step.id);
    const next = nextStepAfter(programme, step.id);
    if (next) progress.setCurrentStep(next.id);

    // Spacing is carried only by clinician-marked pause points + chapter load.
    const ld = landingFor(programme, step);

    const load = chapterLoadFor(module.id);
    const celebratesChapter = completesModule && (load === 'core' || load === 'skill');

    // A reflection beat is its own moment: completing it returns to Today and never
    // chains into the next lesson or a pause landing. When such a reflection ALSO
    // completes the chapter, we persist the chapter-complete signal so Today shows
    // the FULL-SCREEN celebration (the small in-feed tile was retired — full-screen
    // lands better). Skill chapters keep their own skillExit moment instead.
    if (opts?.toToday) {
      if (completesModule) {
        onModuleComplete?.();
        if (ld.kind === 'skillExit') progress.setGuideMasteryDue(true);
        else if (celebratesChapter) {
          const nm = next ? moduleOfStep(programme, next.id) : undefined;
          progress.setChapterCelebration({ moduleId: module.id, next: nm && nm.id !== module.id ? moduleName(nm) : '' });
        }
      }
      if (onExitToToday) onExitToToday(); else onClose();
      return;
    }

    if (completesModule) {
      onModuleComplete?.();
      if (ld.kind === 'skillExit') {
        // A Key Skill chapter just completed → arm H5 mastery for Today (one-shot,
        // survives the home remount this completion triggers).
        progress.setGuideMasteryDue(true);
        setLanding({ kind: 'skillExit' });
      } else {
        const nextMod = next ? moduleOfStep(programme, next.id) : undefined;
        // H8 milestone: a light "well done" face, but only every Nth chapter, and
        // NEVER when this completion crosses an act boundary — there H4 owns the
        // moment (ActBoundary), so H8 stays out of its way.
        const crossesAct = !nextMod || nextMod.course_id !== module.course_id;
        const doneCount = progress.state.completedModules.includes(module.id)
          ? progress.state.completedModules.length
          : progress.state.completedModules.length + 1;
        const showFace = !crossesAct && doneCount % MILESTONE_FACE_EVERY_N === 0;
        setCelebrate({
          nextModuleTitle: nextMod && nextMod.id !== module.id ? moduleName(nextMod) : undefined,
          guide: showFace ? guide.pick('H8_milestone') : null,
        });
      }
    } else if (ld.kind === 'pause') {
      setLanding({ kind: 'pause', handoff: ld.handoff });
    } else if (ld.kind === 'skillExit') {
      setLanding({ kind: 'skillExit' });
    } else {
      onClose();
    }
  };

  if (landing) {
    const next = nextStepAfter(programme, step.id);
    return (
      <PauseLanding
        variant={landing.kind}
        handoff={landing.handoff}
        // "I'm done for now" → stop and land on Today (next step is queued there).
        onDoneForNow={() => (onExitToToday ? onExitToToday() : onClose())}
        // "Keep going" → open the next step straight away (explicit, not autoplay).
        onContinue={() => (next && onOpenStep ? onOpenStep(next) : onClose())}
        onHandoff={(h) => {
          onClose();
          if (h === 'calm') onStartBreathing?.('relax_box');
          else if (h === 'meditation') onOpenMeditations?.();
          // 'journal' → no navigation; the user takes it to their own notebook.
        }}
      />
    );
  }

  if (celebrate) {
    // The "how are you managing?" check-in rides on the celebration as a tile —
    // only at the end of the MAIN (core) chapters, never after intro/summary
    // chapters, and never in the light-touch self-compassion course.
    // Chapters where the check-in is suppressed per clinical review (act3-c12:
    // "remove these how are you handling it questions").
    const CHECKIN_EXCLUDED = ['act3-c12'];
    const isCore =
      programme.id !== 'self-compassion' &&
      chapterLoadFor(module.id) === 'core' &&
      !CHECKIN_EXCLUDED.includes(module.id);
    return (
      <ModuleCelebration
        module={module}
        chapterLabel={chapterLabel}
        nextModuleTitle={celebrate.nextModuleTitle}
        guide={celebrate.guide}
        onGuideShown={() => celebrate.guide && guide.record(celebrate.guide)}
        onContinue={onClose}
        checkIn={
          isCore
            ? {
                onCalmYourBody: onStartBreathing ? () => { onClose(); onStartBreathing('relax_box'); } : undefined,
                onSelfCompassion: onSelfCompassion ? () => { onClose(); onSelfCompassion(); } : undefined,
              }
            : undefined
        }
      />
    );
  }

  if (step.type === 'reflection') {
    return (
      <>
        {reviewBtn}
        <ReflectionPrompt
          step={step}
          module={module}
          initialNote={progress.getReflection(step.id)?.body ?? ''}
          landingFollows={false}
          onSave={(body, meta) => {
            if (body) {
              progress.saveReflection({
                stepId: step.id,
                module: meta.module,
                prompt: meta.prompt,
                body,
                aha: meta.aha,
              });
            }
          }}
          onComplete={() => handleComplete({ toToday: true })}
          onClose={onClose}
        />
      </>
    );
  }

  if (step.type === 'article') {
    return (
      <>
        {reviewBtn}
        <LessonText
          step={step}
          isComplete={isComplete}
          onComplete={handleComplete}
          onClose={onClose}
          disableImageZoom={programme.id === 'self-compassion'}
        />
      </>
    );
  }

  // video / audio / exercise / mixed → the video lesson viewer.
  // If the video has a reflection, it is no longer an easy-to-miss inline button:
  // pressing the video's Complete opens the reflection as the next page, which has
  // its own Complete that finishes the lesson and returns home.
  const reflectionId = `${step.id}-r`;
  const hasReflection = !!step.video_reflection;
  return (
    <>
      {reviewBtn}
      {!showReflection && (
        <VideoLesson
          step={step}
          module={module}
          chapterLabel={chapterLabel}
          author={programme.author}
          initialPosition={progress.getPosition(step.id)}
          isComplete={isComplete}
          onPositionChange={(s) => progress.setPosition(step.id, s)}
          onComplete={hasReflection ? () => setShowReflection(true) : handleComplete}
          onClose={onClose}
        />
      )}
      {showReflection && step.video_reflection && (
        <ReflectionPrompt
          step={{ ...step, id: reflectionId, type: 'reflection', reflection_prompt: step.video_reflection, aha: false, journal: false, image_url: undefined } as Step}
          module={module}
          initialNote={progress.getReflection(reflectionId)?.body ?? ''}
          onSave={(body, meta) => {
            if (body) {
              progress.saveReflection({ stepId: reflectionId, module: meta.module, prompt: meta.prompt, body });
            }
          }}
          onComplete={() => handleComplete({ toToday: true })}
          // A post-video reflection is its own beat — completing OR closing it leaves
          // the lesson entirely (to Today), it does NOT chain to the next lesson or
          // drop back into the video behind it.
          onClose={() => (onExitToToday ? onExitToToday() : onClose())}
        />
      )}
    </>
  );
}
